import "./Chart.css";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface ChartProps {
    data: number[] | any[];
    color?: string;
    width?: string | number;
    height?: string | number;
    showAxes?: boolean;
    showLegend?: boolean;
    series?: Array<{ dataKey: string; name: string; color: string }>;
    loading?: boolean;
    showTooltip?: boolean;
}

export function Chart({ 
    data, 
    color = '#4ade80', 
    width = '100%', 
    height = 60,
    showAxes = false,
    showLegend = false,
    series,
    loading = false,
    showTooltip = true
}: ChartProps) {
    if (loading) {
        return (
            <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="chart-loader"></div>
            </div>
        );
    }
    // Handle simple number array (for sparklines)
    const chartData = Array.isArray(data) && typeof data[0] === 'number'
        ? data.map((value, index) => ({ index, value }))
        : data;

    // Single series mode
    if (!series) {
        return (
            <div className="chart-container" style={{ width, height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                        <defs>
                            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={color} stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="index" hide />
                        <YAxis hide />
                        {showTooltip && (
                            <Tooltip
                                contentStyle={{
                                    background: 'rgba(0, 0, 0, 0.9)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '6px',
                                    padding: '8px 12px'
                                }}
                                labelStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}
                                itemStyle={{ color: color, fontSize: '14px', fontWeight: 600 }}
                                cursor={{ stroke: color, strokeWidth: 1 }}
                            />
                        )}
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke={color}
                            strokeWidth={2}
                            fill={`url(#gradient-${color})`}
                            activeDot={showTooltip ? { r: 5, fill: color, stroke: '#fff', strokeWidth: 2 } : false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // Multi-series mode
    return (
        <div className="chart-container" style={{ width, height }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                        {series.map(s => (
                            <linearGradient key={s.dataKey} id={`gradient-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={s.color} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={s.color} stopOpacity={0}/>
                            </linearGradient>
                        ))}
                    </defs>
                    {showAxes && (
                        <>
                            <XAxis 
                                dataKey="timestamp" 
                                stroke="rgba(255, 255, 255, 0.3)"
                                tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 11 }}
                            />
                            <YAxis 
                                stroke="rgba(255, 255, 255, 0.3)"
                                tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 11 }}
                            />
                        </>
                    )}
                    <Tooltip
                        contentStyle={{
                            background: 'rgba(0, 0, 0, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            padding: '8px 12px'
                        }}
                        labelStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '4px' }}
                    />
                    {showLegend && <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />}
                    {series.map(s => (
                        <Area
                            key={s.dataKey}
                            type="monotone"
                            dataKey={s.dataKey}
                            name={s.name}
                            stroke={s.color}
                            strokeWidth={2}
                            fill={`url(#gradient-${s.dataKey})`}
                            activeDot={{ r: 5, fill: s.color, stroke: '#fff', strokeWidth: 2 }}
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}
